/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import { LEADERBOARD_API } from "@/lib/constants";
import { TLeaderBoardData } from "@/types/leaderboard";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import LeaderBoardError from "./components/error";
import LeaderBoardHeroSection from "./components/hero-section";
import LeadersFilter from "./components/leaders-filter";
import LeaderBoardListing from "./components/listing";
import NoLeaders from "./components/no-leaders";

async function getLeaderBoardData() {
	const res = await fetch(LEADERBOARD_API, {
		next: { revalidate: 10 },
	});

	if (!res.ok) {
		const { message } = await res.json();
		throw new Error(message);
	}

	const { data } = (await res.json()) as { data: TLeaderBoardData };

	return data;
}

const LeaderBoard = () => {
	const { isLoading, isError, data } = useQuery({
		queryKey: ["leaderboard"],
		queryFn: getLeaderBoardData,
		refetchOnWindowFocus: false,
	});

	const [searchResults, setSearchResults] = useState<TLeaderBoardData["leaderboardUsers"]>();
	const [isFetching, setIsFetching] = useState<boolean>(false);

	const noOfVIPsJoinedTillNow = data?.noOfVIPsJoinedTillNow || 0;
	const noOfVIPConnections = data?.noOfVIPConnections || 0;
	const leaderboardUsers = useMemo(() => data?.leaderboardUsers || [], [data]);
	const parentCategories = data?.parentCategories || [];
	const positions = data?.positions || [];

	useEffect(() => {
		setSearchResults(leaderboardUsers);
	}, [leaderboardUsers]);

	if (isError) {
		return <LeaderBoardError />;
	}

	return (
		<div>
			{isLoading ? (
				<Skeleton count={1} height={480} className="w-full" />
			) : (
				<LeaderBoardHeroSection
					noOfVIPsJoinedTillNow={noOfVIPsJoinedTillNow}
					noOfVIPConnections={noOfVIPConnections}
				/>
			)}

			{isLoading ? (
				<div className="mx-auto my-12 max-w-6xl">
					<Skeleton count={1} height={96} className="w-full" />
				</div>
			) : (
				<LeadersFilter
					initialResults={leaderboardUsers}
					parentCategories={parentCategories}
					positions={positions}
					setSearchResults={setSearchResults}
					setIsFetching={setIsFetching}
				/>
			)}

			{isLoading || isFetching ? (
				<div className="mx-auto my-12 grid max-w-6xl grid-cols-6 gap-8">
					{Array.from({ length: 12 }, (_, index) => (
						<Skeleton key={index} count={1} height={128} className="h-48 w-36" />
					))}
				</div>
			) : searchResults && searchResults?.length > 0 ? (
				<LeaderBoardListing users={searchResults} />
			) : (
				<NoLeaders />
			)}
		</div>
	);
};

export default LeaderBoard;
